import { Autocomplete, Box, InputLabel, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  BundlePricing,
  BundleStatus,
  useGetCrmDataQuery,
} from 'generated/graphql';
import React from 'react';

import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';

import { CrmSegmentSelect } from './components/CrmSegmentSelect';
import { SearchBox } from './components/SearchBox';
import { getLeadsSources } from './CreatorCrm.api';
import { useCrmStore } from './CreatorCrm.store';
import { ExportButton } from './CrmExportButton';
import { CrmSegment } from './CrmSegment.enum';
import { useProductOptions } from './hooks/useProductOptions.hook';
import { SourceClassification } from './source-classification.enum';
import { FilterOption } from './filter-option';
import { orderBy } from 'lodash';
import { sprinkles } from '@design-system/sprinkles.css';

type SelectProps = {
  value: FilterOption | FilterOption[] | null;
  onChange: (value: FilterOption[]) => void | ((value: FilterOption) => void);
  options: FilterOption[];
  multiple?: boolean;
  width?: number;
};
function Select({
  value,
  onChange,
  options,
  multiple = false,
  width = 250,
}: SelectProps) {
  return (
    <Autocomplete
      multiple={multiple}
      value={value}
      onChange={(e, v) => onChange(v as FilterOption[])}
      sx={{ width }}
      options={options}
      renderInput={(params) => <TextField {...params} size="small" />}
      renderOption={(props, option: FilterOption) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps}>
            <Box sx={{ flexGrow: 1 }}>{option.label}</Box>
            {option.tag && (
              <Box
                className={sprinkles({
                  color: 'D-100',
                  fontWeight: 'medium',
                })}
              >
                {option.tag}
              </Box>
            )}
          </li>
        );
      }}
    />
  );
}

const selectValue = (
  options: FilterOption[],
  value: string | string[],
  multiple = false,
) => {
  if (multiple) {
    return options.filter((o) => value.includes(String(o.id)));
  }

  return options.find((o) => o?.id === value) ?? null;
};

export function SelectSubscriptionStatus() {
  const crmStore = useCrmStore();

  const subscriptionOptions = [
    { id: 'Cancelled', label: 'Cancelled' },
    { id: 'Active', label: 'Active' },
    { id: 'Inactive', label: 'Inactive' },
    { id: 'BillingFailed', label: 'Billing Failure' },
    crmStore.segment === CrmSegment.ExclusiveBuyers && {
      id: 'None',
      label: 'None',
    },
  ].filter((x) => x) as FilterOption[];

  return (
    <InputLabel>
      <b>Subscription</b>
      <Select
        options={subscriptionOptions}
        value={selectValue(
          subscriptionOptions,
          crmStore.filters.subscription,
          true,
        )}
        onChange={(options: FilterOption[]) =>
          crmStore.handleFilterChange(
            'subscription',
            options.map((option) => option.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectClassification() {
  const crmStore = useCrmStore();

  const classificationOptions = [
    { id: SourceClassification.MigratedSubscription, label: 'Migrated' },
    { id: SourceClassification.FreeSubscription, label: 'Free' },
    { id: SourceClassification.PaidSubscription, label: 'Paid' },
    { id: SourceClassification.TrialSubscription, label: 'Trial' },
    { id: SourceClassification.BulkUpdateSubscription, label: 'Bulk Uploads' },
  ];

  return (
    <InputLabel>
      <b>Classification</b>
      <Select
        options={classificationOptions}
        value={selectValue(
          classificationOptions,
          crmStore.filters.classification,
          true,
        )}
        onChange={(options: FilterOption[]) =>
          crmStore.handleFilterChange(
            'classification',
            options.map((option) => option.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectProduct() {
  const crmStore = useCrmStore();
  const products = useProductOptions();

  return (
    <InputLabel>
      <b>Product</b>
      <Select
        options={products}
        value={selectValue(products, crmStore.filters.product, true)}
        onChange={(options: FilterOption[]) =>
          crmStore.handleFilterChange(
            'product',
            options.map((p) => p.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectAdditionalLabel() {
  const crmStore = useCrmStore();

  const additionalLabels = [
    { id: 'Recently converted', label: 'Recently converted' },
    { id: 'Recently churned', label: 'Recently churned' },
    { id: 'First purchase', label: 'First purchase' },
  ];

  return (
    <InputLabel>
      <b>Additional</b>
      <Select
        options={additionalLabels}
        value={selectValue(additionalLabels, crmStore.filters.additional, true)}
        onChange={(options: FilterOption[]) =>
          crmStore.handleFilterChange(
            'additional',
            options.map((option) => option.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectOTPs() {
  const crmStore = useCrmStore();
  const creatorId = useInfluencerId();
  const result = useGetCrmDataQuery({
    variables: {
      creatorId,
    },
  });

  const programOptions: FilterOption[] =
    result.data?.creator?.publishedPrograms
      ?.filter((item) => item.accessType === 'exclusive')
      .map((p) => ({
        id: `${p.id}:program`,
        label: p.name as string,
      })) ?? [];

  const allowedBundleStatuses = [BundleStatus.Published, BundleStatus.Archived];
  const bundleOptions: FilterOption[] =
    result.data?.creator?.bundle
      ?.filter(
        (bundle) =>
          bundle.pricing === BundlePricing.Otp &&
          allowedBundleStatuses.includes(bundle.status),
      )
      .map((bundle) => ({
        id: `${bundle.id}:otp-bundle`,
        label: bundle.title,
        tag: 'OFFER',
      })) ?? [];

  const otpOptions = orderBy(
    [...bundleOptions, ...programOptions],
    'label',
    'asc',
  );

  return (
    <InputLabel>
      <b>OTP</b>
      <Select
        options={otpOptions}
        value={selectValue(otpOptions, crmStore.filters.otpOffers, true)}
        onChange={(options: FilterOption[]) =>
          crmStore.handleFilterChange(
            'otpOffers',
            options.map((option) => option.id),
          )
        }
        width={300}
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectLeadsSource() {
  const crmStore = useCrmStore();
  const { data } = useQuery(['crm', 'leadsSources'], getLeadsSources);

  const leadSources = data ?? [];

  const leadsSourcesOptions: FilterOption[] = [];

  leadSources.forEach((ls) =>
    leadsSourcesOptions.push({
      id: ls.value,
      label: ls.title,
    }),
  );

  return (
    <InputLabel>
      <b>Source</b>
      <Select
        width={200}
        options={leadsSourcesOptions}
        value={selectValue(
          leadsSourcesOptions,
          crmStore.filters.leadsSource,
          true,
        )}
        onChange={(options: FilterOption[]) =>
          crmStore.handleFilterChange(
            'leadsSource',
            options.map((option) => option.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

const saveAsHash = (fn: any) => (v: any) => {
  window.location.hash = v;
  return fn(v);
};

type FiltersBarProps = {
  totalResults: number;
  children: React.ReactNode;
};
export function FiltersBar({ children, totalResults }: FiltersBarProps) {
  const crmStore = useCrmStore();

  return (
    <div className="flex flex-wrap gap-5 md:w-full">
      <section>
        <InputLabel>
          <b>Segments</b>
          <CrmSegmentSelect
            activeFilter={crmStore.segment}
            setActiveFilter={saveAsHash(crmStore.setActiveSegment)}
          />
        </InputLabel>
      </section>
      <section className="w-full md:w-auto flex flex-col md:flex-row gap-4">
        {children}
      </section>
      <section className="w-full md:w-auto flex items-start gap-4 justify-between">
        <div className="w-full md:w-auto min-w-[200px]">
          <InputLabel>
            <span>
              <b>Search</b>
              <span className="ml-2 text-[#8A8E99]">
                {totalResults} Results
              </span>
            </span>
            <SearchBox
              onChange={crmStore.setSearchTerm}
              value={crmStore.searchTerm}
            />
          </InputLabel>
        </div>

        <ExportButton />
      </section>
    </div>
  );
}
